import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { 
  AlertIcon, FeelIcon, CircleOfSoulsIcon, CheckIcon, 
  MessageIcon, ShieldIcon, ArrowIcon 
} from './BespokeIcons';
import { toast } from 'sonner';

/**
 * EmpathySOS Component
 * 
 * An SOS/alert button that sends help requests to verified empathy helpers.
 * Includes a legal disclaimer that users must accept before requesting help.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Legal Disclaimer Text
const DISCLAIMER_TEXT = `
**Empathy Support Disclaimer**

By requesting empathy support through Belonging Sessions, you understand and agree to the following:

1. **Not Professional Therapy:** The helpers responding to your request are trained volunteers in compassionate communication (NVC), NOT licensed therapists, counselors, or mental health professionals. This service is peer-to-peer emotional support, not professional treatment.

2. **Crisis Resources:** If you are experiencing a mental health crisis, suicidal thoughts, or are in immediate danger, please contact emergency services (911) or the National Suicide Prevention Lifeline (988) immediately.

3. **Confidentiality:** While our helpers commit to maintaining confidentiality, this is a volunteer service and cannot guarantee the same legal protections as licensed professional services.

4. **No Guarantees:** We cannot guarantee response times, availability of helpers, or outcomes. Helpers offer their time voluntarily.

5. **Your Responsibility:** You are responsible for your own decisions and actions. The guidance offered is meant to support your own self-reflection and communication.

6. **Respect & Safety:** Both parties agree to maintain respectful communication. Any inappropriate behavior will result in removal from the platform.

By proceeding, you acknowledge that you have read, understood, and agree to these terms.
`;

const URGENCY_LEVELS = [
  { id: 'normal', label: 'Whenever Available', description: 'General support needed, no rush', color: 'emerald' },
  { id: 'soon', label: 'Within 24 Hours', description: 'Situation is difficult but manageable', color: 'amber' },
  { id: 'urgent', label: 'As Soon as Possible', description: 'Really struggling right now', color: 'rose' }
];

const CONTACT_PREFERENCES = [
  { id: 'messaging', label: 'Text Messaging', icon: MessageIcon },
  { id: 'video', label: 'Video Call', icon: CircleOfSoulsIcon },
];

const EmpathySOS = ({ 
  userId,
  userName,
  triggerVariant = 'default', // 'default', 'footer', 'floating'
  className = ''
}) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: disclaimer, 2: form, 3: confirmation
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    situation: '',
    urgency: 'normal',
    contactPreference: 'messaging'
  });
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    if (!formData.situation.trim()) {
      toast.error('Please describe your situation briefly');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/empathy-alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId || localStorage.getItem('elchme_user_id') || 'anonymous',
          user_name: userName || localStorage.getItem('elchme_user_name') || 'Anonymous',
          situation_summary: formData.situation,
          urgency_level: formData.urgency,
          contact_preference: formData.contactPreference,
          disclaimer_accepted: disclaimerAccepted
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setStep(3);
        toast.success('Help request sent successfully');
      } else {
        throw new Error('Failed to send request');
      }
    } catch (error) {
      console.error('Empathy alert error:', error);
      toast.error('Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setDisclaimerAccepted(false);
    setFormData({ situation: '', urgency: 'normal', contactPreference: 'messaging' });
    setResult(null);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(resetForm, 300);
  };

  // Render trigger button based on variant
  const renderTrigger = () => {
    if (triggerVariant === 'footer') {
      return (
        <button
          className={`flex items-center gap-2 text-rose-400/70 hover:text-rose-400 transition-colors ${className}`}
          data-testid="empathy-sos-footer-btn"
        >
          <CircleOfSoulsIcon size={16} color="#FB7185" />
          <span className="text-sm">Need Empathy Support?</span>
        </button>
      );
    }

    if (triggerVariant === 'floating') {
      return (
        <button
          className={`fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 shadow-lg shadow-rose-500/30 flex items-center justify-center hover:scale-110 transition-transform z-50 ${className}`}
          data-testid="empathy-sos-floating-btn"
          title="Request Empathy Support"
        >
          <CircleOfSoulsIcon size={28} color="#FFFFFF" />
        </button>
      );
    }

    return (
      <Button
        variant="outline"
        className={`border-rose-500/30 text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/50 ${className}`}
        data-testid="empathy-sos-btn"
      >
        <CircleOfSoulsIcon size={16} color="#FB7185" className="mr-2" />
        Request Empathy Support
      </Button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {renderTrigger()}
      </DialogTrigger>

      <DialogContent 
        className="bg-[#0f1419] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="empathy-sos-dialog"
      >
        {/* Step 1: Disclaimer */}
        {step === 1 && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg text-rose-400" style={{ fontFamily: "'Playfair Display', serif" }}>
                <ShieldIcon size={24} color="#FB7185" />
                Before We Connect You
              </DialogTitle>
              <DialogDescription className="text-white/60">
                Please read and accept the following to continue
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              {/* Crisis Notice */}
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertIcon size={20} color="#FBBF24" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-400 font-medium text-sm">Crisis Resources</p>
                    <p className="text-white/60 text-xs mt-1">
                      If you're in immediate danger or experiencing a mental health crisis, please call <strong>911</strong> or <strong>988</strong> (Suicide Prevention Lifeline).
                    </p>
                  </div>
                </div>
              </div>

              {/* Disclaimer Text */}
              <div className="p-4 bg-white/5 border border-white/10 rounded-lg max-h-[250px] overflow-y-auto">
                <div className="prose prose-sm prose-invert">
                  {DISCLAIMER_TEXT.split('\n').map((line, i) => {
                    if (line.startsWith('**') && line.endsWith('**')) {
                      return <h4 key={i} className="text-white font-semibold mt-3 mb-2">{line.replace(/\*\*/g, '')}</h4>;
                    }
                    if (line.match(/^\d\./)) {
                      return <p key={i} className="text-white/70 text-sm mb-2">{line}</p>;
                    }
                    return line.trim() ? <p key={i} className="text-white/70 text-sm mb-2">{line}</p> : null;
                  })}
                </div>
              </div>

              {/* Accept Checkbox */}
              <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                <Checkbox
                  id="disclaimer"
                  checked={disclaimerAccepted}
                  onCheckedChange={setDisclaimerAccepted}
                  className="mt-0.5 border-rose-500/50 data-[state=checked]:bg-rose-500"
                />
                <label htmlFor="disclaimer" className="text-sm text-white/80 cursor-pointer">
                  I have read, understood, and agree to the terms above. I understand this is peer support, not professional therapy.
                </label>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="ghost" onClick={handleClose} className="text-white/50">
                Cancel
              </Button>
              <Button
                onClick={() => setStep(2)}
                disabled={!disclaimerAccepted}
                className={disclaimerAccepted 
                  ? "bg-rose-500 hover:bg-rose-400 text-white" 
                  : "bg-white/10 text-white/40 cursor-not-allowed"
                }
              >
                I Accept, Continue
                <ArrowIcon size={16} color="#FFFFFF" direction="right" className="ml-2" />
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step 2: Request Form */}
        {step === 2 && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg text-rose-400" style={{ fontFamily: "'Playfair Display', serif" }}>
                <CircleOfSoulsIcon size={24} color="#FB7185" />
                Request Empathy Support
              </DialogTitle>
              <DialogDescription className="text-white/60">
                A verified helper will reach out to you
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-5">
              {/* Situation Description */}
              <div>
                <label className="text-sm text-white/70 mb-2 block">
                  What's going on? (Brief summary)
                </label>
                <Textarea
                  value={formData.situation}
                  onChange={(e) => setFormData(prev => ({ ...prev, situation: e.target.value }))}
                  placeholder="I'm having a difficult conversation with... / I'm feeling overwhelmed about... / I need help understanding..."
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[100px]"
                  maxLength={500}
                />
                <p className="text-white/30 text-xs mt-1 text-right">{formData.situation.length}/500</p>
              </div>

              {/* Urgency Level */}
              <div>
                <label className="text-sm text-white/70 mb-2 block">How urgent is this?</label>
                <div className="grid gap-2">
                  {URGENCY_LEVELS.map((level) => (
                    <button
                      key={level.id}
                      onClick={() => setFormData(prev => ({ ...prev, urgency: level.id }))}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        formData.urgency === level.id
                          ? level.color === 'emerald' ? 'bg-emerald-500/20 border-emerald-500/50'
                            : level.color === 'amber' ? 'bg-amber-500/20 border-amber-500/50'
                            : 'bg-rose-500/20 border-rose-500/50'
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium text-sm ${
                          formData.urgency === level.id
                            ? level.color === 'emerald' ? 'text-emerald-400'
                              : level.color === 'amber' ? 'text-amber-400'
                              : 'text-rose-400'
                            : 'text-white/80'
                        }`}>{level.label}</span>
                        {formData.urgency === level.id && (
                          <CheckIcon size={16} color={
                            level.color === 'emerald' ? '#4ADE80'
                            : level.color === 'amber' ? '#FBBF24'
                            : '#FB7185'
                          } />
                        )}
                      </div>
                      <p className="text-white/50 text-xs mt-1">{level.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Preference */}
              <div>
                <label className="text-sm text-white/70 mb-2 block">How would you like to connect?</label>
                <div className="flex gap-2">
                  {CONTACT_PREFERENCES.map((pref) => {
                    const Icon = pref.icon;
                    return (
                      <button
                        key={pref.id}
                        onClick={() => setFormData(prev => ({ ...prev, contactPreference: pref.id }))}
                        className={`flex-1 p-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                          formData.contactPreference === pref.id
                            ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                            : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
                        }`}
                      >
                        <Icon size={18} color={formData.contactPreference === pref.id ? '#3B82F6' : '#9CA3AF'} />
                        <span className="text-sm">{pref.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Soul Reminder */}
              <div className="p-4 bg-purple-900/20 border border-purple-500/20 rounded-lg">
                <p className="text-purple-300/90 text-sm italic leading-relaxed">
                  "Remember: this isn't about apps or exercises—it's a state of mind. When each person connects with their own soul and the souls of those involved, we become receptive to solutions we didn't see coming."
                </p>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="ghost" onClick={() => setStep(1)} className="text-white/50">
                <ArrowIcon size={16} color="#9CA3AF" direction="left" className="mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !formData.situation.trim()}
                className="bg-rose-500 hover:bg-rose-400 text-white"
              >
                {loading ? (
                  <>Sending Request...</>
                ) : (
                  <>
                    <CircleOfSoulsIcon size={16} color="#FFFFFF" className="mr-2" />
                    Send Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && result && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>
                <CheckIcon size={24} color="#4ADE80" />
                Request Sent
              </DialogTitle>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              <div className="p-4 bg-emerald-900/20 border border-emerald-500/20 rounded-lg text-center">
                <CircleOfSoulsIcon size={48} color="#4ADE80" className="mx-auto mb-3" />
                <h3 className="text-emerald-400 font-medium mb-2">Your request has been received</h3>
                <p className="text-white/70 text-sm">
                  Verified helpers have been notified. Someone will reach out to you soon.
                </p>
              </div>

              <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                <p className="text-white/60 text-sm mb-3">
                  <strong className="text-white/80">What happens next:</strong>
                </p>
                <ul className="space-y-2 text-sm text-white/70">
                  <li className="flex items-start gap-2">
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px] mt-0.5">1</Badge>
                    A verified helper will review your request
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px] mt-0.5">2</Badge>
                    They'll reach out via your preferred method
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px] mt-0.5">3</Badge>
                    Together, you can use ElchmE to work through the situation
                  </li>
                </ul>
              </div>

              {result.redirect_url && (
                <a
                  href={result.redirect_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full p-3 bg-purple-500/20 border border-purple-500/30 rounded-lg text-center text-purple-300 hover:bg-purple-500/30 transition-colors"
                >
                  <MessageIcon size={16} color="#A855F7" className="inline mr-2" />
                  Go to Belonging Sessions Messaging →
                </a>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button onClick={handleClose} className="w-full bg-white/10 hover:bg-white/20 text-white">
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmpathySOS;
